import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import PropTypes from 'prop-types';

/**
 * An accessible info-icon tooltip. Renders a small "ⓘ" icon that reveals
 * explanatory text on hover or keyboard focus. Useful for domain terms and
 * non-obvious rules.
 *
 * `flip` is required, not optional: OverlayTrigger only enables Popper's flip
 * modifier by default when `placement` contains "auto", so a fixed "top"
 * placement would stay above the icon and clip against the top of the
 * viewport (or the modal's scroll box) instead of flipping below it.
 *
 * The color utility is required, not cosmetic: custom.scss forces every
 * non-outline `.btn` to near-white ($btn-font-color), which leaves btn-link
 * invisible on a light background. The utility's `!important` restores a
 * readable color. Pass variant="light" on dark surfaces (the moderator pages
 * use text-white headings), where text-primary would be just as unreadable.
 *
 * Accessibility:
 *  - The icon is decorative (`aria-hidden="true"`).
 *  - The `<button>` carries `aria-label` so screen readers always announce
 *    the explanation, even without hovering.
 *  - Triggers on both hover and focus (WCAG 2.1 AA 1.4.13).
 *
 * Usage:
 *   <span>Karma score <InfoTooltip text="Karma is earned by contributing to communities." /></span>
 */
export default function InfoTooltip({
    text,
    placement = 'top',
    variant = 'primary',
}) {
    const colorClass = variant === 'light' ? 'text-white' : 'text-primary';

    return (
        <OverlayTrigger
            placement={placement}
            flip
            trigger={['hover', 'focus']}
            overlay={<Tooltip>{text}</Tooltip>}
        >
            <button
                type="button"
                className={`btn btn-link ${colorClass} p-0 ms-1 align-baseline`}
                aria-label={text}
            >
                <i className="bi bi-info-circle" aria-hidden="true" />
            </button>
        </OverlayTrigger>
    );
}

InfoTooltip.propTypes = {
    /** The explanatory text shown in the tooltip and exposed to screen readers. */
    text: PropTypes.string.isRequired,
    /** Tooltip placement relative to the icon. */
    placement: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
    /** Icon color. Use 'light' on dark surfaces, 'primary' on light ones. */
    variant: PropTypes.oneOf(['primary', 'light']),
};
